import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Play, Pause, Disc3 } from 'lucide-react';
import { useSpin } from '../../context/SpinContext';
import { usePreviewLayout } from '../../hooks/usePreviewLayout';
import { useBackgroundLayer, useDiscFaceLayer, useForegroundLayer } from '../../hooks/useCanvasLayers';
import { useRotationLoop } from '../../hooks/useRotationLoop';

// Live preview architecture: background / disc-face / sheen / foreground are
// each rendered to their OWN small canvas exactly once whenever their
// relevant settings change (see useCanvasLayers). Rotation itself is applied
// purely via a CSS `transform: rotate()` on the disc-face canvas element,
// updated every frame directly through a ref (see useRotationLoop) --
// changing the RPM slider never triggers a React re-render or texture redraw.
export default function PreviewCanvas() {
  const { disc, background, foreground, exportSettings, audio } = useSpin();
  const wrapperRef = useRef(null);
  const bgCanvasRef = useRef(null);
  const discCanvasRef = useRef(null);
  const sheenCanvasRef = useRef(null);
  const fgCanvasRef = useRef(null);

  const rpmRef = useRef(disc.rpm);
  const audioRef = useRef(audio);
  const [freeRunPlaying, setFreeRunPlaying] = useState(true);
  const freeRunPlayingRef = useRef(true);
  const [availableWidth, setAvailableWidth] = useState(320);

  rpmRef.current = disc.reverse ? -disc.rpm : disc.rpm;
  audioRef.current = audio;
  freeRunPlayingRef.current = freeRunPlaying;

  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const update = () => setAvailableWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  const { cssW, cssH, discOutputPx, placeholderIconPx, placeholderFontPx } =
    usePreviewLayout(exportSettings, disc, availableWidth);

  const isPlaying = audio.hasAudio ? audio.isPlaying : freeRunPlaying;

  useBackgroundLayer(bgCanvasRef, background, cssW, cssH);
  useDiscFaceLayer(discCanvasRef, sheenCanvasRef, disc, discOutputPx);
  useForegroundLayer(fgCanvasRef, foreground, cssW, cssH);

  const getIsPlaying = useCallback(() => {
    const a = audioRef.current;
    return a.hasAudio ? a.isPlaying : freeRunPlayingRef.current;
  }, []);
  useRotationLoop(discCanvasRef, rpmRef, getIsPlaying);

  const togglePlay = () => {
    if (audio.hasAudio) {
      audio.isPlaying ? audio.pause() : audio.play();
    } else {
      setFreeRunPlaying((p) => !p);
    }
  };

  return (
    <div ref={wrapperRef} className="flex flex-col items-center gap-4 w-full">
      <div
        className="relative rounded-2xl overflow-hidden shadow-[0_8px_40px_rgba(0,0,0,0.25)] ring-1 ring-black/5 max-w-full"
        style={{ width: cssW, height: cssH }}
      >
        <canvas ref={bgCanvasRef} className="absolute inset-0 block max-w-full" />
        <canvas
          ref={discCanvasRef}
          style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', willChange: 'transform' }}
        />
        {disc.designMode === 'vinyl' && (
          <canvas
            ref={sheenCanvasRef}
            className="pointer-events-none"
            style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)' }}
          />
        )}
        <canvas ref={fgCanvasRef} className="absolute inset-0 block max-w-full pointer-events-none" />
        {!disc.imageEl && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-white/70 px-4 text-center" style={{ gap: Math.max(6, Math.round(Math.min(cssW, cssH) * 0.02)) }}>
            <Disc3
              className="animate-spin-slow flex-shrink-0"
              strokeWidth={1.5}
              style={{ width: placeholderIconPx, height: placeholderIconPx }}
            />
            <p className="font-medium leading-snug" style={{ fontSize: placeholderFontPx }}>Upload disc art to get started</p>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={togglePlay}
        className="w-11 h-11 rounded-full bg-neutral-900 hover:bg-neutral-800 text-white flex items-center justify-center transition-colors shadow-md flex-shrink-0"
      >
        {isPlaying ? <Pause className="w-[18px] h-[18px]" fill="currentColor" /> : <Play className="w-[18px] h-[18px] ml-0.5" fill="currentColor" />}
      </button>
    </div>
  );
}
